import React, { useMemo, useState, useEffect } from "react";
import { MoreVertical } from "lucide-react";
import { useCustomerCashRecords } from "../../../hooks/useCashExchange";
import { CashInOutRecord } from "../../../api/services/cashExchange/type";
import { Pagination } from "../../common/Pagination";

interface CashInOutTableProps {
  customerId?: string;
  selectedDate?: string;
  selectedDateFrom?: string;
  selectedDateTo?: string;
}

export const CashInOutTable: React.FC<CashInOutTableProps> = ({
  customerId,
  selectedDate,
  selectedDateFrom,
  selectedDateTo,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  const getQueryParams = () => {
    const params: {
      skip: number;
      limit: number;
      date?: string;
      fromDate?: string;
      toDate?: string;
      recordType: "cash_in" | "cash_out" | "all";
    } = {
      skip: (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage,
      recordType: "all",
    };

    if (selectedDateFrom && selectedDateTo) {
      params.fromDate = selectedDateFrom;
      params.toDate = selectedDateTo;
    } else if (selectedDate) {
      params.date = selectedDate;
    }

    return params;
  };

  const {
    data: cashRecordsData,
    isLoading,
    isError,
    error,
  } = useCustomerCashRecords(customerId || "", getQueryParams());

  const combinedRecords = useMemo(() => {
    if (!cashRecordsData?.items) return [];

    return cashRecordsData.items
      .map(
        (record): CashInOutRecord => ({
          id: record.id,
          type: record.recordType === "cash_in" ? "cash-in" : "cash-out",
          amount: record.amount,
          date: record.date,
          shift: record.shift,
          paymentType: record.paymentType,
          currency: record.currency,
          foreignExchangeRate: record.foreignExchangeRate,
          agentId: record.agentId,
        }),
      )
      .sort((a, b) => {
        const dateCompare =
          new Date(b.date).getTime() - new Date(a.date).getTime();
        if (dateCompare !== 0) return dateCompare;
        return a.shift.localeCompare(b.shift);
      });
  }, [cashRecordsData?.items]);

  const paginatedRecords = useMemo(() => {
    return combinedRecords;
  }, [combinedRecords]);

  const totals = useMemo(() => {
    return combinedRecords.reduce(
      (acc, record) => {
        if (record.type === "cash-in") {
          acc.totalCashIn += record.amount;
        } else {
          acc.totalCashOut += record.amount;
        }
        return acc;
      },
      { totalCashIn: 0, totalCashOut: 0 },
    );
  }, [combinedRecords]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDate, selectedDateFrom, selectedDateTo]);

  const totalPages = cashRecordsData?.pagination
    ? Math.ceil(cashRecordsData.pagination.total / itemsPerPage)
    : 1;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getDateFilterText = () => {
    if (selectedDate) {
      return `Date: ${formatDate(selectedDate)}`;
    } else if (selectedDateFrom && selectedDateTo) {
      return `Date Range: ${formatDate(selectedDateFrom)} - ${formatDate(
        selectedDateTo,
      )}`;
    }
    return null;
  };

  const hasActiveFilters = Boolean(
    selectedDate || (selectedDateFrom && selectedDateTo),
  );

  if (isLoading) {
    return (
      <div className="bg-slate-800 rounded-b-lg overflow-visible">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-slate-400">
            Loading cash exchange records...
          </span>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-slate-800 rounded-b-lg overflow-visible">
        <div className="flex items-center justify-center h-64">
          <div className="text-red-400">
            Error loading records: {error?.message || "Unknown error"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-b-lg">
      {/* Show active filters */}
      {hasActiveFilters && (
        <div className="px-6 py-3 border-b border-slate-700 bg-slate-750">
          <div className="text-sm text-slate-300 flex flex-wrap gap-4">
            <span className="text-slate-400">Active filters:</span>
            {getDateFilterText() && (
              <span className="text-purple-400">{getDateFilterText()}</span>
            )}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-700">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                Shift
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                Cash-In
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                Cash-Out
              </th>
              <th className="px-6 py-4 text-center text-xs font-medium text-slate-300 uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {paginatedRecords.map((record) => (
              <tr
                key={`${record.type}-${record.id}`}
                className="hover:bg-slate-700 transition-colors"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                  {formatDate(record.date)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {record.shift}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-slate-300">
                  {record.type === "cash-in" ? (
                    <span className="text-green-400 font-medium">
                      {formatCurrency(record.amount)}
                    </span>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-slate-300">
                  {record.type === "cash-out" ? (
                    <span className="text-red-400 font-medium">
                      {formatCurrency(record.amount)}
                    </span>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <button className="text-slate-400 hover:text-white transition-colors">
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-900">
            <tr>
              <td
                className="px-6 py-4 text-sm font-semibold text-yellow-400"
                colSpan={2}
              >
                Total
              </td>
              <td className="px-6 py-4 text-right text-sm font-semibold text-green-400">
                {formatCurrency(totals.totalCashIn)}
              </td>
              <td className="px-6 py-4 text-right text-sm font-semibold text-red-400">
                {formatCurrency(totals.totalCashOut)}
              </td>
              <td className="px-6 py-4"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {paginatedRecords.length === 0 && (
        <div className="text-center py-12">
          <div className="text-slate-400">
            {hasActiveFilters
              ? "No cash exchange records found with the selected filters"
              : "No cash exchange records found"}
          </div>
        </div>
      )}

      {/* Pagination - Server-side pagination */}
      {(cashRecordsData?.pagination?.total || 0) > itemsPerPage && (
        <div className="mt-6 px-6 pb-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};
